import { Trans, useTranslation } from 'next-i18next'

import FaqAccordion, { ItemLink } from './faq-accordion-new'

interface Props {
  trackingId?: string
}

const LandlordsFaq: React.FC<Props> = ({ trackingId }) => {
  const { t } = useTranslation('pricing')
  const LANDLORD_TIPS = [
    {
      title: t('pricing:How much does it cost to list on liv.rent'),
      answers: [
        {
          steps: [
            <Trans
              key="You can post as many listings as you want on liv.rent for free"
              i18nKey="pricing:You can post as many listings as you want on liv.rent for free"
            >
              You can post as many listings as you want on liv.rent for free. To
              take advantage of our full suite of rental management tools, our
              pricing plans start at $48/month with an annual subscription plan.
            </Trans>,
            <Trans
              key="Want to learn more? Book an info session"
              i18nKey="pricing:Want to learn more? Book an info session"
            >
              Want to learn more?{' '}
              <ItemLink
                href="https://calendly.com/livrent/30min-educational-session?utm_source=livrent&utm_medium=landlordlanding&utm_campaign=organic_livrent_landlord_FAQ&month=2022-01"
                rel="noopener noreferrer"
                target="_blank"
              >
                Book an info session
              </ItemLink>{' '}
              instead and speak with our team of Rental Experts about how
              liv.rent can help you market and manage your listings.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('pricing:How do you advertise a rental property? '),
      answers: [
        {
          steps: [
            <Trans
              key="liv.rent helps landlords and property managers advertise their"
              i18nKey="pricing:liv.rent helps landlords and property managers advertise their"
            >
              <ItemLink
                href="https://liv.rent/"
                rel="noopener noreferrer"
                target="_blank"
              >
                liv.rent
              </ItemLink>{' '}
              helps landlords and property managers advertise their rental
              property online. With a built-in{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/10/verification-process/"
                rel="noopener noreferrer"
                target="_blank"
              >
                verification process
              </ItemLink>
              , you have access to a pool of pre-screened tenants. To increase
              exposure, you can share and cross-promote your rental listing to
              liv.rent’s syndicated channels (Facebook Marketplace, Facebook
              Rental Groups, WeChat, Craigslist, Kijiji, Zumper & more). You can
              also highlight the unique features of your property with our
              rental listing template and free video tour uploads. To price your
              rental competitively, make sure you book an appointment for a{' '}
              <ItemLink
                href="https://landlords.liv.rent/rentestimate/"
                rel="noopener noreferrer"
                target="_blank"
              >
                free rental estimate.
              </ItemLink>
            </Trans>
          ]
        }
      ]
    },
    {
      title: t(
        'pricing:Why does liv.rent ask for so much personal information?'
      ),
      answers: [
        {
          steps: [
            <Trans
              key="The information we ask for is exactly what you’ll find on all rental websites"
              i18nKey="pricing:The information we ask for is exactly what you’ll find on all rental websites"
            >
              The information we ask for is exactly what you’ll find on all
              rental websites. We don’t ask for anything that you wouldn’t share
              with a tenant if you wanted to get into a rental agreement. We
              take your personal information very seriously.{' '}
              <ItemLink
                href="https://liv.rent/privacy#personal-information"
                rel="noopener noreferrer"
                target="_blank"
              >
                Click here to learn more about our privacy policy.
              </ItemLink>
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('pricing:How can liv.rent help me find tenants?'),
      answers: [
        {
          steps: [
            <Trans
              key="You can list your property on liv.rent to leverage our suggested tenants feature"
              i18nKey="pricing:You can list your property on liv.rent to leverage our suggested tenants feature"
            >
              You can list your property on liv.rent to leverage our{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/08/suggested-tenants/"
                rel="noopener noreferrer"
                target="_blank"
              >
                suggested tenants
              </ItemLink>{' '}
              feature, and instantly connect with thousands of pre-screened
              tenants. liv.rent also enables multi-platform syndication, so you
              can share your listing across different platforms (Craigslist,
              Kijiji, Zumper, Facebook Marketplace & more). For more exposure,
              you can showcase your property as a &quot;Sponsored Listing&quot;
              on liv.rent and/or have your own Corporate Page. Depending on your
              needs, liv.rent can help you create your own personal rental
              website.
            </Trans>,
            <Trans
              key="Contact info@liv.rent for more information."
              i18nKey="pricing:Contact info@liv.rent for more information."
            >
              Contact{' '}
              <ItemLink
                href="mailto:info@liv.rent"
                rel="noopener noreferrer"
                target="_blank"
              >
                info@liv.rent
              </ItemLink>{' '}
              for more information.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('pricing:What is the Trust Score?'),
      answers: [
        {
          steps: [
            <Trans
              key="The Trust Score is an intelligent rating system designed by liv.rent to establish"
              i18nKey="pricing:The Trust Score is an intelligent rating system designed by liv.rent to establish"
            >
              The Trust Score is an intelligent rating system designed by
              liv.rent to establish a renter’s reliability and credibility.
              Unlike other credit check reports, Trust Score provides a
              comprehensive picture of potential tenants. Using AI and a
              powerful algorithm, this system accounts for a renter’s financial
              record as well as public and submitted data to rate the renter’s
              suitability and simplify the tenant selection process.
              <ItemLink
                href="https://liv.rent/blog/2019/10/trust-score-explained/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Click here to learn more about Trust Score.
              </ItemLink>
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('pricing:How do I collect rent from my renters? '),
      answers: [
        {
          steps: [
            <Trans
              key="If your tenant is paying by Visa, Mastercard, or American Express"
              i18nKey="pricing:If your tenant is paying by Visa, Mastercard, or American Express"
            >
              If your tenant is paying by Visa, Mastercard, or American Express,
              then you can accept payments through{' '}
              <ItemLink
                href="https://stripe.com/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Stripe
              </ItemLink>
              . All you need to do is connect your Stripe account with liv.rent
              and once your tenant makes a payment, it will be deposited into
              your Stripe account, which pays it out to your bank account. If
              your tenant chooses to pay by UnionPay or bitcoin, then you can
              set up a direct deposit by putting your bank account information
              in your liv.rent profile.
            </Trans>
          ]
        }
      ]
    }
  ]

  return <FaqAccordion data={LANDLORD_TIPS} trackingId={trackingId} />
}

export default LandlordsFaq
